"use client";
import { createContext, type HTMLAttributes, type ReactNode, useContext, useState } from "react";
import { cn } from "./cn";

interface TabContext {
  value: string;
  setValue: (v: string) => void;
}

const Ctx = createContext<TabContext | null>(null);

export interface TabListProps extends HTMLAttributes<HTMLDivElement> {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  ariaLabel: string;
  children: ReactNode;
}

export function TabList({
  value: controlled,
  defaultValue = "",
  onValueChange,
  ariaLabel,
  className,
  children,
  ...props
}: TabListProps) {
  const [internal, setInternal] = useState(defaultValue);
  const value = controlled ?? internal;
  const setValue = (v: string) => {
    if (controlled === undefined) setInternal(v);
    onValueChange?.(v);
  };
  return (
    <Ctx.Provider value={{ value, setValue }}>
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={cn("inline-flex items-center gap-1 flex-wrap", className)}
        {...props}
      >
        {children}
      </div>
    </Ctx.Provider>
  );
}

export interface TabProps extends Omit<HTMLAttributes<HTMLButtonElement>, "onClick"> {
  value: string;
}

export function Tab({ value, className, children, ...props }: TabProps) {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("Tab must be used inside <TabList>");
  const active = ctx.value === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => ctx.setValue(value)}
      className={cn(
        "px-3 py-1.5 rounded-md text-button-sm font-medium transition-colors",
        active
          ? "bg-brand-fill text-white"
          : "text-ink-muted hover:text-ink hover:bg-surface-muted",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}
